import { useEffect, useRef, useState } from 'react';
import { useAbsenceStore } from '../store/useAbsenceStore';

const DEBOUNCE_MS = 300;

/**
 * Live-Vorschau der Arbeitstage eines Abwesenheitsantrags (FAFD-7). Fragt den Server erst, wenn
 * beide Daten gesetzt sind und das Ende nicht vor dem Start liegt; ohne `employeeId` gilt der
 * angemeldete Mitarbeiter. `null` = (noch) keine Vorschau.
 */
export function useWorkingDaysPreview(startDate: string, endDate: string, employeeId?: string) {
  const previewWorkingDays = useAbsenceStore((s) => s.previewWorkingDays);
  const [workingDays, setWorkingDays] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const sequenceRef = useRef(0);

  // why: the admin form needs an employee first; an incomplete or reversed range has no preview.
  const active = !!startDate && !!endDate && endDate >= startDate && employeeId !== '';

  useEffect(() => {
    if (!active) {
      sequenceRef.current += 1;
      setWorkingDays(null);
      setLoading(false);
      return;
    }

    const sequence = ++sequenceRef.current;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const days = await previewWorkingDays(startDate, endDate, employeeId);
        if (sequence === sequenceRef.current) setWorkingDays(days);
      } catch (error) {
        console.error('[useWorkingDaysPreview] preview failed', error);
        if (sequence === sequenceRef.current) setWorkingDays(null);
      } finally {
        if (sequence === sequenceRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      // why: invalidate the in-flight sequence so a late response cannot overwrite a newer one.
      if (sequence === sequenceRef.current) sequenceRef.current += 1;
    };
  }, [active, startDate, endDate, employeeId, previewWorkingDays]);

  return { workingDays, loading };
}
